import React, { useState, useEffect } from 'react';
import { User, UserCreate, UserUpdate } from '../types';
import { apiService } from '../services/api';

interface Server {
  id: number;
  name: string;
}

interface Company {
  id: number;
  name: string;
}

interface UserFormProps {
  initialData?: User | null;
  onSubmit: (data: UserCreate | UserUpdate) => void;
  onCancel: () => void;
  isLoading?: boolean;
  currentUserRole?: string;
}

const UserForm: React.FC<UserFormProps> = ({ 
  initialData, 
  onSubmit, 
  onCancel, 
  isLoading = false,
  currentUserRole = 'user'
}) => {
  const [formData, setFormData] = useState({
    username: initialData?.username || '',
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    password: '',
    confirmPassword: '',
    access: initialData?.access || 'user',
    // For backward compatibility - these will be used to create assignments
    server_id: null as number | null,
    company_id: null as number | null,
    preferred_language: initialData?.preferred_language || 'en',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
  });

  const [servers, setServers] = useState<Server[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    fetchServersAndCompanies();
  }, []);

  const fetchServersAndCompanies = async () => {
    setLoadingData(true);
    try {
      const [serversResponse, companiesResponse] = await Promise.all([
        apiService.getAllServers(100).catch(() => [] as any[]),
        apiService.getCompanies(0, 100).catch(() => [] as any[])
      ]);
      setServers(serversResponse);
      setCompanies(companiesResponse);
    } catch (error) {
      console.error('Error fetching servers and companies:', error);
      setErrors({ general: 'Failed to load servers and companies' });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
               (name === 'server_id' || name === 'company_id') ? 
               (value ? parseInt(value) : null) : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic validation
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation (only for new users or when password is being changed)
    if (!initialData || formData.password) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else {
        if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        }
        if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(formData.password)) {
          newErrors.password = 'Password must contain at least one letter and one number';
        }
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Required fields for creation
    if (!initialData) {
      if (!formData.server_id) newErrors.server_id = 'Server is required';
      if (!formData.company_id) newErrors.company_id = 'Company is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const submitData: UserCreate | UserUpdate = {
      username: formData.username.trim(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || undefined,
      preferred_language: formData.preferred_language,
      is_active: formData.is_active,
      access: formData.access,
      ...(formData.server_id && { server_id: formData.server_id }),
      ...(formData.company_id && { company_id: formData.company_id }),
      ...(formData.password && { password: formData.password }),
    };

    onSubmit(submitData);
  };

  const isAdmin = currentUserRole === 'admin';
  const isManager = currentUserRole === 'manager';
  const canEditRole = isAdmin;
  const canEditServerCompany = isAdmin || isManager;

  if (loadingData) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading form data...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="form">
      {errors.general && (
        <div className="alert alert-error">
          {errors.general}
        </div>
      )}

      <div className="form-section">
        <h3>Basic Information</h3>
        
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="username">Username *</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className={`form-control ${errors.username ? 'error' : ''}`}
              placeholder="Enter username"
              disabled={isLoading}
            />
            {errors.username && <span className="error-message">{errors.username}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`form-control ${errors.email ? 'error' : ''}`}
              placeholder="Enter email address"
              disabled={isLoading}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="first_name">First Name *</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={`form-control ${errors.first_name ? 'error' : ''}`}
              placeholder="Enter first name"
              disabled={isLoading}
            />
            {errors.first_name && <span className="error-message">{errors.first_name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="last_name">Last Name *</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={`form-control ${errors.last_name ? 'error' : ''}`}
              placeholder="Enter last name"
              disabled={isLoading}
            />
            {errors.last_name && <span className="error-message">{errors.last_name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter phone number"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="preferred_language">Preferred Language</label>
            <select
              id="preferred_language"
              name="preferred_language"
              value={formData.preferred_language}
              onChange={handleChange}
              className="form-control"
              disabled={isLoading}
            >
              <option value="en">English</option>
              <option value="de">Deutsch (German)</option>
              <option value="pt">Português (Portuguese)</option>
              <option value="pt-BR">Português (Brasil)</option>
              <option value="es">Español (Spanish)</option>
              <option value="fr">Français (French)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Security & Access</h3>
        
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="password">
              {initialData ? 'New Password (leave empty to keep current)' : 'Password *'}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-control ${errors.password ? 'error' : ''}`}
              placeholder={initialData ? 'Enter new password' : 'Enter password'}
              disabled={isLoading}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
            <small className="form-help">
              Password must be at least 8 characters with letters and numbers
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              {initialData ? 'Confirm New Password' : 'Confirm Password *'}
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`form-control ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Confirm password"
              disabled={isLoading}
            />
            {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
          </div>

          {canEditRole && (
            <div className="form-group">
              <label htmlFor="access">Role *</label>
              <select
                id="access"
                name="access"
                value={formData.access}
                onChange={handleChange}
                className="form-control"
                disabled={isLoading}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <small className="form-help">
                Admin: Full access | Manager: Limited to assigned resources | User: Basic access
              </small>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={isLoading}
              />
              Active
            </label>
            <small className="form-help">
              Inactive users cannot log in to the system
            </small>
          </div>
        </div>
      </div>

      {canEditServerCompany && (
        <div className="form-section">
          <h3>Assignments</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="server_id">Server {!initialData ? '*' : ''}</label>
              <select
                id="server_id"
                name="server_id"
                value={formData.server_id || ''}
                onChange={handleChange}
                className={`form-control ${errors.server_id ? 'error' : ''}`}
                disabled={isLoading}
              >
                <option value="">Select a server</option>
                {servers.map(server => (
                  <option key={server.id} value={server.id}>
                    {server.name}
                  </option>
                ))}
              </select>
              {errors.server_id && <span className="error-message">{errors.server_id}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company_id">Company {!initialData ? '*' : ''}</label>
              <select
                id="company_id"
                name="company_id"
                value={formData.company_id || ''}
                onChange={handleChange}
                className={`form-control ${errors.company_id ? 'error' : ''}`}
                disabled={isLoading}
              >
                <option value="">Select a company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {errors.company_id && <span className="error-message">{errors.company_id}</span>}
            </div>
          </div>
        </div>
      )}

      {initialData && (
        <div className="form-section">
          <h3>User Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>User ID:</label>
              <span>{initialData.id}</span>
            </div>
            <div className="info-item">
              <label>Created:</label>
              <span>{new Date(initialData.created_at).toLocaleDateString()}</span>
            </div>
            <div className="info-item">
              <label>Last Updated:</label>
              <span>
                {initialData.updated_at 
                  ? new Date(initialData.updated_at).toLocaleDateString()
                  : 'Never'
                }
              </span>
            </div>
            <div className="info-item">
              <label>Last Login:</label>
              <span>
                {initialData.last_login 
                  ? new Date(initialData.last_login).toLocaleDateString()
                  : 'Never'
                }
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="form-actions">
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : (initialData ? 'Update User' : 'Create User')}
        </button>
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UserForm;